import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, Button, Modal, ModalHeader, ModalBody } from 'flowbite-react';
import { useAsync } from 'react-async-hook';
import { Node, Edge } from '@xyflow/react';
import { FiEye, FiEyeOff, FiMoreVertical, FiGrid, FiArrowUp, FiCalendar, FiSliders } from 'react-icons/fi';

import * as sched from 'sched';
import { useAuth } from '../../hooks/useAuth';
import { useDatabase } from '../../hooks/useDatabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useWorkflowInstance } from '../../hooks/useWorkflowInstance';
import { useTemplateCache } from '../../hooks/useTemplateCache';
import WorkflowGraph, { getNodeDisplayContext, WorkflowGraphNodeData } from '../../components/workflow/WorkflowGraph';
import { FuzzyGanttChart, type FuzzyScheduleResult } from '../../components/charts/FuzzyGanttChart';
import { WorkflowParameterForm } from '../../components/workflow/WorkflowParameterForm';
import { WorkflowStepStatusModal } from '../../components/workflow/WorkflowStepStatusModal';
import { evaluateStepCondition, getExcludedStepIds } from '../../utils/workflowConditions';

// ─── Shared assignment resolution ─────────────────────────────────────────

interface ResolvedAssignment {
  /** The display name (person/team name, "Everyone", or a warning) */
  primary: string;
  /** If sourced from a parameter, the parameter key */
  paramKey?: string;
  /** True if the parameter is unset */
  missing?: boolean;
}

type NameLookup = Array<{ id: string; name: string }>;

function resolveSubject(
  subject: any,
  parameterValues: Record<string, any>,
  people: NameLookup,
  teams: NameLookup,
): ResolvedAssignment {
  if (!subject) return { primary: '—' };
  if (subject.type === 'Everyone') return { primary: 'Everyone' };
  if (subject.type === 'Users') {
    const ids: string[] = subject.ids || [];
    const names = ids.map(id => people.find(p => p.id === id)?.name ?? id.slice(0, 8));
    return { primary: names.join(', ') || '—' };
  }
  if (subject.type === 'Team') {
    const name = teams.find(t => t.id === subject.id)?.name ?? subject.id.slice(0, 8);
    return { primary: name };
  }
  if (subject.type === 'Parameter') {
    const key = subject.key;
    const val = parameterValues[key];
    if (val == null) return { primary: 'Not set', paramKey: key, missing: true };
    // ParameterValue::Worker → { type: "Worker", value: { type: "Person"|"Team", id } }
    const ref = val?.value;
    if (ref?.type === 'Person') {
      const name = people.find(p => p.id === ref.id)?.name ?? ref.id.slice(0, 8);
      return { primary: name, paramKey: key };
    }
    if (ref?.type === 'Team') {
      const name = teams.find(t => t.id === ref.id)?.name ?? ref.id.slice(0, 8);
      return { primary: name, paramKey: key };
    }
    return { primary: 'Not set', paramKey: key, missing: true };
  }
  return { primary: subject.type || '—' };
}

function resolveStepAssignment(
  step: sched.WorkflowTemplateStep,
  parameterValues: Record<string, any>,
  people: NameLookup,
  teams: NameLookup,
): ResolvedAssignment {
  const c = step.completion as any;
  if (c.type === 'Manual') return resolveSubject(c.by, parameterValues, people, teams);
  if (c.type === 'Branch') return resolveSubject(c.decider, parameterValues, people, teams);
  if (c.type === 'Gate') return resolveSubject(c.updater, parameterValues, people, teams);
  if (c.type === 'ExternalEvent') return resolveSubject(c.updater, parameterValues, people, teams);
  return { primary: '—' };
}

const AssignmentCell: React.FC<{ assignment: ResolvedAssignment }> = ({ assignment }) => (
  <div>
    <span className={assignment.missing ? 'text-amber-500' : ''}>
      {assignment.primary}
    </span>
    {assignment.paramKey && (
      <span className="block text-xs text-cm-text-muted">{assignment.paramKey}</span>
    )}
  </div>
);

// ─── Read-only step table ─────────────────────────────────────────────────

const InstanceStepTable: React.FC<{
  templateState: sched.WorkflowTemplateState | null;
  parameterValues: Record<string, any>;
  stepStates: Record<string, any>;
  excludedStepIds: Set<string>;
  showExcluded: boolean;
  people: NameLookup;
  teams: NameLookup;
  onStepClick: (step: sched.WorkflowTemplateStep) => void;
}> = ({ templateState, parameterValues, stepStates, excludedStepIds, showExcluded, people, teams, onStepClick }) => {
  if (!templateState) return null;

  const thClass = 'px-3 py-2 text-left text-xs font-medium text-cm-text-muted uppercase tracking-wider';
  const tdClass = 'px-3 py-2 text-sm text-cm-text-primary';

  const steps = templateState.steps.filter(step =>
    showExcluded || !excludedStepIds.has(step.id)
  );

  const stepStatus = (stepId: string): { label: string; color: string } => {
    if (excludedStepIds.has(stepId)) return { label: 'Excluded', color: 'bg-cm-bg-hover text-cm-text-muted' };
    const state = stepStates[stepId];
    if (state?.completed_datetime) return { label: 'Completed', color: 'bg-green-100 text-green-800 dark:bg-green-900/30' };
    return { label: 'Pending', color: 'bg-cm-bg-hover text-cm-text-primary' };
  };

  const stepTypeColor = (type: string) => {
    if (type === 'Manual') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30';
    if (type === 'Branch') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
    if (type === 'TimeElapsed') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    if (type === 'Gate') return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300';
    if (type === 'ExternalEvent') return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
    return 'bg-cm-bg-hover text-cm-text-primary';
  };

  return (
    <div className="h-full overflow-auto p-4">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-cm-bg-elevated sticky top-0 z-10">
          <tr>
            <th className={thClass}>Title</th>
            <th className={thClass}>Type</th>
            <th className={thClass}>Assignment</th>
            <th className={thClass}>Estimate</th>
            <th className={thClass}>Condition</th>
            <th className={thClass}>Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {steps.map(step => {
            const isExcluded = excludedStepIds.has(step.id);
            const est = step.completion.type === 'Manual' ? (step.completion as any).work_estimate : null;
            const status = stepStatus(step.id);
            const rowClass = isExcluded ? 'opacity-40' : 'hover:bg-cm-bg-elevated/50 cursor-pointer';

            const assignment = resolveStepAssignment(step, parameterValues, people, teams);

            return (
              <tr key={step.id} className={rowClass} onClick={() => !isExcluded && onStepClick(step)}>
                <td className={tdClass}>{step.title}</td>
                <td className={tdClass}>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${stepTypeColor(step.completion.type)}`}>
                    {step.completion.type}
                  </span>
                </td>
                <td className={`${tdClass} text-xs`}><AssignmentCell assignment={assignment} /></td>
                <td className={tdClass}>
                  {est?.expected != null ? (
                    <span className="text-xs">{est.min ?? '?'}/{est.expected}/{est.max ?? '?'}</span>
                  ) : (
                    <span className="text-cm-text-muted">—</span>
                  )}
                </td>
                <td className={tdClass}>
                  {step.condition ? (
                    <span className="text-xs text-cm-text-muted">
                      {step.condition.type === 'BoolParameter'
                        ? `${(step.condition as any).negate ? '!' : ''}${(step.condition as any).key}`
                        : step.condition.type}
                    </span>
                  ) : null}
                </td>
                <td className={tdClass}>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {steps.length === 0 && (
        <p className="text-center text-cm-text-muted py-8 text-sm">No steps to display.</p>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────

const WorkflowInstanceDetailPage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { client, token } = useAuth();
  const { database, applyCommand, workspaceId } = useDatabase();
  const { buildUrl } = useWorkspace();
  const { instance, loading, error } = useWorkflowInstance(instanceId);
  const templateCache = useTemplateCache();

  // UI state
  const [showParams, setShowParams] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<'graph' | 'table'>('graph');
  const [selectedStep, setSelectedStep] = useState<sched.WorkflowTemplateStep | null>(null);
  const [scheduleResult, setScheduleResult] = useState<any | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load user profiles for parameter form
  const userProfilesResponse = useAsync(async () => {
    if (!client || !token || !workspaceId) return [];
    return (await sched.WrappedApiClient.list_workspace_user_profiles(client, token, workspaceId)).user_profiles;
  }, [client, token, workspaceId]);

  // Resolve template state from cache
  const templateStateResponse = useAsync(async () => {
    if (!instance) return null;
    const version = instance.versionPreference === 'Pin' ? instance.lockedVersion : undefined;
    return await templateCache.getTemplateState(instance.templateId, version);
  }, [instance?.templateId, instance?.lockedVersion, instance?.versionPreference]);

  const templateState = templateStateResponse.result ?? null;
  const parameterValues = instance?.parameterValues ?? {};
  const stepStates = instance?.stepStates ?? {};

  // Fetch latest template version to offer upgrade
  const latestVersionResponse = useAsync(async () => {
    if (!instance || !client || !token) return null;
    const events = await sched.WrappedApiClient.list_workflow_template_events(
      client, token, instance.templateId
    );
    return events.events.length;
  }, [instance?.templateId, client, token]);
  const latestVersion = latestVersionResponse.result ?? instance?.lockedVersion ?? 0;
  const hasUpgrade = instance != null && latestVersion > instance.lockedVersion;

  // Compute which steps are excluded by conditions
  const excludedStepIds = useMemo(() => {
    if (!templateState) return new Set<string>();
    return getExcludedStepIds(templateState, parameterValues);
  }, [templateState, parameterValues]);

  // Count missing parameters
  const discoveredParams = useMemo(() => {
    if (!templateState) return [];
    const arr = sched.discover_workflow_parameters(templateState);
    return Array.from(arr).map((obj: any) => ({
      key: obj.key as string,
      type: obj.type as string,
    }));
  }, [templateState]);
  // Set title_template for pre-existing instances that don't have it yet
  useEffect(() => {
    if (!instanceId || !instance || !templateState?.title) return;
    if (instance.titleTemplate) return; // already set
    applyCommand({
      type: 'UpdateWorkflowInstance',
      id: instanceId,
      actions: [{ type: 'SetTitleTemplate', title_template: templateState?.title }],
    });
  }, [instanceId, instance?.titleTemplate, templateState?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-set unset Bool parameters to false so they aren't "missing"
  useEffect(() => {
    if (!instanceId || discoveredParams.length === 0) return;
    const unsetBools = discoveredParams.filter(
      p => p.type === 'Bool' && parameterValues[p.key] == null
    );
    if (unsetBools.length === 0) return;
    const actions = unsetBools.map(p => ({
      type: 'SetParameterValue' as const,
      key: p.key,
      value: { type: 'Bool', value: false },
    }));
    const command: sched.DatabaseCommand = {
      type: 'UpdateWorkflowInstance',
      id: instanceId,
      actions,
    };
    applyCommand(command);
  }, [instanceId, discoveredParams, parameterValues, applyCommand]);

  const missingParamCount = discoveredParams.filter(p => parameterValues[p.key] == null).length;

  // Build graph nodes and edges
  const { graphNodes, graphEdges } = useMemo(() => {
    if (!templateState) return { graphNodes: [], graphEdges: [] };

    const nodes: Node<WorkflowGraphNodeData>[] = templateState.steps
      .filter((step) => {
        // When excluded nodes are hidden, remove them entirely from the graph
        if (!showExcluded && excludedStepIds.has(step.id)) return false;
        return true;
      })
      .map((step) => {
        const condResult = evaluateStepCondition(step, parameterValues);
        const isExcluded = condResult === 'excluded';
        const isUnknown = condResult === 'unknown';

        const baseContext = getNodeDisplayContext(userProfilesResponse.result || [], step, undefined);

        // Completion & work status
        const state = stepStates[step.id];
        const isCompleted = !!state?.completed_datetime;
        const entityKey = instanceId ? `step:${instanceId}:${step.id}` : null;
        const hasWork = entityKey && database
          ? (() => { const a = database.manual_allocations_for(entityKey); return Array.isArray(a) && a.length > 0; })()
          : false;

        // When showing excluded nodes, dim them; otherwise they've been filtered out above
        const conditionClass = isExcluded
          ? 'opacity-30 saturate-0'
          : isUnknown && step.condition
            ? 'opacity-60'
            : '';

        return {
          id: step.id,
          type: 'workflowStep',
          position: { x: 0, y: 0 },
          width: 200,
          data: {
            step,
            displayContext: {
              ...baseContext,
              className: [baseContext.className, conditionClass].filter(Boolean).join(' ') || undefined,
              completedAt: state?.completed_datetime ?? undefined,
              animateLoadingSpinner: !isCompleted && !isExcluded && !!hasWork,
            },
          },
          draggable: false,
        };
      });

    const nodeIdSet = new Set(nodes.map(n => n.id));

    const edges: Edge[] = templateState.links
      .filter((link) => nodeIdSet.has(link.source_step_id) && nodeIdSet.has(link.target_step_id))
      .map((link) => {
        const touchesExcluded =
          excludedStepIds.has(link.source_step_id) || excludedStepIds.has(link.target_step_id);

        return {
          id: `${link.source_step_id}-${link.target_step_id}`,
          source: link.source_step_id,
          sourceHandle: link.source_step_branch_id || undefined,
          target: link.target_step_id,
          type: 'default',
          animated: !touchesExcluded,
          style: touchesExcluded
            ? { strokeDasharray: '6 3', opacity: 0.3 }
            : undefined,
        };
      });

    return { graphNodes: nodes, graphEdges: edges };
  }, [templateState, parameterValues, stepStates, excludedStepIds, showExcluded, userProfilesResponse.result, database, instanceId]);

  // Command helpers
  const setParameter = useCallback(async (key: string, value: any) => {
    if (!instanceId) return;
    await applyCommand({
      type: 'UpdateWorkflowInstance',
      id: instanceId,
      actions: [{ type: 'SetParameterValue', key, value }],
    });
  }, [instanceId, applyCommand]);

  const removeParameter = useCallback(async (key: string) => {
    if (!instanceId) return;
    await applyCommand({
      type: 'UpdateWorkflowInstance',
      id: instanceId,
      actions: [{ type: 'RemoveParameterValue', key }],
    });
  }, [instanceId, applyCommand]);

  const updateStepState = useCallback(async (stepId: string, state: any) => {
    if (!instanceId) return;
    const command: sched.DatabaseCommand = {
      type: 'UpdateWorkflowInstance',
      id: instanceId,
      actions: [{ type: 'SetStepState', step_id: stepId, state }],
    };
    await applyCommand(command);
  }, [instanceId, applyCommand]);

  const upgradeToLatest = useCallback(async () => {
    if (!instanceId || !latestVersion || !instance || !client || !token) return;
    console.log('[upgradeToLatest] current lockedVersion:', instance.lockedVersion, 'target:', latestVersion);
    const actions: any[] = [{ type: 'SetLockedVersion', version: latestVersion }];
    // Fetch the latest template metadata to get the (possibly changed) title
    try {
      const tmpl = await sched.WrappedApiClient.get_workflow_template(client, token, instance.templateId);
      actions.push({ type: 'SetTitleTemplate', title_template: tmpl.title });
    } catch { /* title template will remain as-is */ }
    console.log('[upgradeToLatest] sending UpdateWorkflowInstance with actions:', JSON.stringify(actions));
    await applyCommand({
      type: 'UpdateWorkflowInstance',
      id: instanceId,
      actions,
    });
    console.log('[upgradeToLatest] command applied successfully');
  }, [instanceId, instance, latestVersion, applyCommand, client, token]);

  // Build database schedule
  const buildSchedule = useCallback(() => {
    if (!database) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      const result = sched.build_database_fuzzy_schedule(
        database,
        templateCache.wrappedCache,
        today,
        0.5,
        undefined, // use default units_per_day
      );
      setScheduleResult(result);
      setScheduleError(null);
    } catch (err) {
      console.error('Failed to build database schedule:', err);
      setScheduleError(String(err));
      setScheduleResult(null);
    }
  }, [database, templateCache.wrappedCache, instance?.lockedVersion, templateState]);

  // Rebuild schedule when modal opens or data changes
  useEffect(() => {
    if (showSchedule) buildSchedule();
  }, [showSchedule, buildSchedule]);

  // People and teams for parameter form
  const people = useMemo(() => {
    if (!database) return [];
    const ps = database.people();
    return Object.entries(ps).map(([id, p]: [string, any]) => ({
      id,
      name: p.name(),
    }));
  }, [database]);

  const teams = useMemo(() => {
    if (!database) return [];
    const ts = database.teams();
    return Object.entries(ts).map(([id, t]: [string, any]) => ({
      id,
      name: t.name(),
    }));
  }, [database]);

  // Close menu on click outside
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as globalThis.Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // Loading state
  if (loading || templateStateResponse.loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !instance) {
    return (
      <div className="p-4 text-red-600">
        {error ?? 'Workflow instance not found'}
      </div>
    );
  }

  if (templateStateResponse.error) {
    return (
      <div className="p-4 text-red-600">
        Failed to load template: {templateStateResponse.error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-cm-border-subtle">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-cm-text-primary">
              {instance.title || templateState?.title || 'Untitled'}
            </h1>
            <p className="text-sm text-cm-text-muted">
              <Link
                to={buildUrl({ kind: 'workflow-template-edit', templateId: instance.templateId })}
                className="hover:text-blue-500 hover:underline"
              >
                v{instance.lockedVersion}
              </Link>
              {instance.versionPreference === 'FollowLatest' && ' (following latest)'}
              {hasUpgrade && (
                <span className="ml-1 text-blue-500">
                  · v{latestVersion} available
                </span>
              )}
              {' · '}
              Created {new Date(instance.createdAt).toLocaleDateString()}
            </p>
          </div>

          {/* Graph / Table toggle */}
          <div className="flex rounded-md overflow-hidden border border-cm-border-subtle">
            <button
              onClick={() => setViewMode('graph')}
              className={`px-2.5 py-1.5 text-xs font-medium ${viewMode === 'graph' ? 'bg-blue-600 text-white' : 'bg-cm-bg-surface text-cm-text-primary hover:bg-cm-bg-hover'}`}
            >Graph</button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-2.5 py-1.5 text-xs font-medium border-l border-cm-border-subtle ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-cm-bg-surface text-cm-text-primary hover:bg-cm-bg-hover'}`}
            >
              <FiGrid className="w-3.5 h-3.5 inline mr-1" />Table
            </button>
          </div>
        </div>

        {/* Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-md text-cm-text-muted hover:bg-cm-bg-hover"
          >
            <FiMoreVertical className="w-5 h-5" />
            {(missingParamCount > 0 || hasUpgrade) && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
            )}
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-cm-bg-surface border border-cm-border-subtle rounded-lg shadow-lg z-50 py-1">
              <button
                onClick={() => { setShowParams(true); setShowMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-cm-bg-hover text-cm-text-primary"
              >
                <FiSliders className="w-4 h-4" />
                Parameters
                {missingParamCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-amber-500 rounded-full">
                    {missingParamCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => { setShowSchedule(true); setShowMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-cm-bg-hover text-cm-text-primary"
              >
                <FiCalendar className="w-4 h-4" />
                Schedule
              </button>
              {excludedStepIds.size > 0 && (
                <button
                  onClick={() => { setShowExcluded(!showExcluded); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-cm-bg-hover text-cm-text-primary"
                >
                  {showExcluded ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                  {showExcluded ? 'Hide' : 'Show'} {excludedStepIds.size} excluded
                </button>
              )}
              {hasUpgrade && (
                <>
                  <div className="border-t border-cm-border-subtle my-1" />
                  <button
                    onClick={() => { upgradeToLatest(); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-cm-bg-hover text-blue-600"
                  >
                    <FiArrowUp className="w-4 h-4" />
                    Upgrade to v{latestVersion}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Graph or Table */}
      <div className="flex-1 min-h-0">
        {viewMode === 'graph' ? (
          templateState && graphNodes.length > 0 ? (
            <WorkflowGraph
              nodes={graphNodes}
              edges={graphEdges}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={true}
              enableAutoLayout={true}
              onNodeClick={(_event, node) => {
                setSelectedStep(node.data.step);
              }}
              className="h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-cm-text-muted">
              {templateState ? 'No steps in this template' : 'Loading template...'}
            </div>
          )
        ) : (
          <InstanceStepTable
            templateState={templateState}
            parameterValues={parameterValues}
            stepStates={stepStates}
            excludedStepIds={excludedStepIds}
            showExcluded={showExcluded}
            people={people}
            teams={teams}
            onStepClick={setSelectedStep}
          />
        )}
      </div>

      {/* Parameter Form Modal */}
      {templateState && (
        <WorkflowParameterForm
          show={showParams}
          onClose={() => setShowParams(false)}
          templateState={templateState}
          parameterValues={parameterValues}
          onSetParameter={setParameter}
          onRemoveParameter={removeParameter}
          onSaveBatch={(sets, removes) => {
            if (!instanceId) return;
            const actions: any[] = [
              ...sets.map(({ key, value }) => ({ type: 'SetParameterValue', key, value })),
              ...removes.map(key => ({ type: 'RemoveParameterValue', key })),
            ];
            if (actions.length > 0) {
              applyCommand({ type: 'UpdateWorkflowInstance', id: instanceId, actions });
            }
          }}
          people={people}
          teams={teams}
        />
      )}

      {/* Step Status Modal */}
      {selectedStep && instanceId && (
        <WorkflowStepStatusModal
          show={true}
          onClose={() => setSelectedStep(null)}
          step={selectedStep}
          stepState={stepStates[selectedStep.id] ?? null}
          onUpdateStepState={updateStepState}
          resolvedAssignment={resolveStepAssignment(selectedStep, parameterValues, people, teams)}
          instanceId={instanceId}
          database={database}
          people={people}
          applyCommand={applyCommand}
        />
      )}

      {/* Schedule Modal */}
      <Modal show={showSchedule} onClose={() => setShowSchedule(false)} size="7xl">
        <ModalHeader>Database Schedule Projection</ModalHeader>
        <ModalBody>
          {scheduleError && (
            <div className="text-sm text-red-500 mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded">
              {scheduleError}
            </div>
          )}
          {scheduleResult && Object.keys(scheduleResult.schedule.items).length > 0 ? (
            <FuzzyGanttChart
              result={adaptDatabaseScheduleForGantt(scheduleResult, database)}
              startDate={new Date()}
            />
          ) : scheduleResult && !scheduleError ? (
            <ScheduleDiagnostics
              scheduleResult={scheduleResult}
              instanceId={instanceId!}
              database={database}
            />
          ) : !scheduleError ? (
            <div className="text-sm text-cm-text-muted p-8 text-center">Building schedule...</div>
          ) : null}
          {scheduleResult && Object.keys(scheduleResult.schedule.unschedulable).length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
              <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Unschedulable Items ({Object.keys(scheduleResult.schedule.unschedulable).length})
              </h4>
              <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1">
                {Object.entries(scheduleResult.schedule.unschedulable).map(([nodeId, item]: [string, any]) => (
                  <li key={nodeId}>
                    <span className="font-medium">{item.label}</span>: {item.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

// ─── Schedule diagnostics ─────────────────────────────────────────────────

const ScheduleDiagnostics: React.FC<{
  scheduleResult: any;
  instanceId: string;
  database: sched.WrappedDatabase | null;
}> = ({ scheduleResult, instanceId, database }) => {
  const hints: string[] = [];
  const meta = scheduleResult?.meta;
  const nodeCount = meta?.node_labels ? Object.keys(meta.node_labels).length : 0;
  const unschedulableCount = scheduleResult?.schedule?.unschedulable
    ? Object.keys(scheduleResult.schedule.unschedulable).length
    : 0;

  if (nodeCount === 0) {
    // No nodes at all — the instance probably isn't prioritized
    hints.push(
      'No items were included in the schedule. The workflow instance must be added to the priority list (Priorities page) before it can be scheduled.'
    );
  } else if (unschedulableCount > 0 && unschedulableCount === nodeCount) {
    // All nodes are unschedulable — check common reasons
    const reasons = Object.values(scheduleResult.schedule.unschedulable) as any[];
    const noWorkers = reasons.filter(r => r.reason?.includes('No workers'));
    const noEstimate = reasons.filter(r => r.reason?.includes('Missing work estimate'));
    const blocked = reasons.filter(r => r.reason?.includes('Blocked'));

    if (noWorkers.length > 0) {
      hints.push(
        `${noWorkers.length} step(s) have no assigned workers. Check that all "Parameter" worker assignments have corresponding parameter values set, and that those people exist in the database.`
      );
    }
    if (noEstimate.length > 0) {
      hints.push(
        `${noEstimate.length} step(s) have no work estimate. Add min/expected/max estimates in the template editor.`
      );
    }
    if (blocked.length > 0) {
      hints.push(
        `${blocked.length} step(s) are blocked by unresolved decisions or upstream failures.`
      );
    }
  } else {
    hints.push('No items could be scheduled. Check the unschedulable items list below for details.');
  }

  // Check if template might not be loaded
  if (nodeCount === 0 && hints.length <= 1) {
    hints.push(
      'If you recently created this workflow, try reloading the page — the template may not have been loaded yet.'
    );
  }

  return (
    <div className="p-6 space-y-3">
      <p className="text-sm font-medium text-cm-text-primary">
        No items could be scheduled
      </p>
      {hints.map((hint, i) => (
        <p key={i} className="text-sm text-cm-text-muted">
          {hint}
        </p>
      ))}
    </div>
  );
};

/**
 * Adapt a DatabaseFuzzyScheduleResult to the FuzzyScheduleResult shape expected
 * by FuzzyGanttChart. Maps person-based resources to pool-like grouping.
 */
function adaptDatabaseScheduleForGantt(dbResult: any, database?: sched.WrappedDatabase | null): FuzzyScheduleResult {
  const meta = dbResult.meta;
  const items = dbResult.schedule.items as Record<string, any>;

  // Build resource→person name mapping
  const resourceToPerson: Record<string, string> = {};
  for (const [resourceId, personId] of Object.entries(meta.resource_to_person as Record<string, string>)) {
    resourceToPerson[resourceId] = (meta.person_labels as Record<string, string>)[personId] || personId;
  }

  // Detect resource pools: group resources that always co-occur on the same tasks.
  // For each node, collect the set of contributing resource IDs. Resources that share
  // the same eligibility set across all tasks form a natural pool (e.g., team members).
  const resourceSetByNode: Record<string, string[]> = {};
  for (const [nodeId, item] of Object.entries(items)) {
    const rids = Object.keys((item as any).resource_contributions);
    if (rids.length > 0) {
      resourceSetByNode[nodeId] = rids.sort();
    }
  }

  // Group resources into pools by their co-occurrence signature
  const signatureToResources: Record<string, Set<string>> = {};
  for (const rids of Object.values(resourceSetByNode)) {
    const sig = rids.join(',');
    if (!signatureToResources[sig]) signatureToResources[sig] = new Set();
    for (const rid of rids) signatureToResources[sig].add(rid);
  }

  // Build team lookup: team name → set of member person IDs
  const teamMembers: Record<string, Set<string>> = {};
  if (database) {
    try {
      const teams = database.teams();
      for (const [, team] of Object.entries(teams as Record<string, any>)) {
        const members = team.member_ids?.() ?? [];
        if (members.length > 0) {
          teamMembers[team.name()] = new Set(members as string[]);
        }
      }
    } catch { /* teams() may not be available */ }
  }

  // For each unique pool, create a label — use team name if the pool's
  // person set exactly matches a team's member set
  const poolResources: Record<string, string[]> = {};
  const resourceToPool: Record<string, string> = {};

  const assignedResources = new Set<string>();
  for (const [, members] of Object.entries(signatureToResources)) {
    if (members.size > 1) {
      const personIds = new Set(
        [...members].map(rid => (meta.resource_to_person as Record<string, string>)[rid]).filter(Boolean)
      );

      // Find a team whose members exactly match this pool's person set
      const matchingTeam = Object.entries(teamMembers).find(([, teamSet]) =>
        teamSet.size === personIds.size && [...personIds].every(pid => teamSet.has(pid))
      );

      const poolLabel = matchingTeam
        ? matchingTeam[0]
        : [...members].map(rid => resourceToPerson[rid] || rid).sort().join(', ');

      if (!poolResources[poolLabel]) poolResources[poolLabel] = [];
      for (const rid of members) {
        if (!assignedResources.has(rid)) {
          poolResources[poolLabel].push(rid);
          resourceToPool[rid] = poolLabel;
          assignedResources.add(rid);
        }
      }
    }
  }

  // Then, assign remaining resources as individual pools
  for (const [resourceId, personName] of Object.entries(resourceToPerson)) {
    if (!assignedResources.has(resourceId)) {
      if (!poolResources[personName]) poolResources[personName] = [];
      poolResources[personName].push(resourceId);
      resourceToPool[resourceId] = personName;
    }
  }

  return {
    schedule: dbResult.schedule,
    meta: {
      node_to_step: {},
      node_labels: meta.node_labels,
      resource_to_pool: resourceToPool,
      pool_resources: poolResources,
      conditional_nodes: [],
    },
    stats: dbResult.stats,
  };
}

export default WorkflowInstanceDetailPage;
